// /tenant-picker — pre-session tenant chooser for the Ring App Store
// integration. Hits the BFF GET /api/tenant-picker/candidates (pre-session
// cookie scoped). On select, POSTs to /api/tenant-picker/activate which
// exchanges the pre-session cookie for a full session and either redirects
// the browser or returns a `redirect_to` JSON envelope.

import { useEffect, useState } from "react";
import { ApiError } from "@/api";
import { tenantPickerClient } from "@/api/tenantPickerClient";
import { TenantCard } from "@/components/TenantCard";
import type { TenantCandidate } from "@/types/tenant";
import "./tenant-picker.css";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; candidates: TenantCandidate[] }
  | { kind: "error"; message: string }
  | { kind: "expired" };

export function TenantPickerPage(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [activating, setActivating] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        const candidates = await tenantPickerClient.listCandidates(ctrl.signal);
        setState({ kind: "ready", candidates });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof ApiError && err.status === 401) {
          setState({ kind: "expired" });
          return;
        }
        const msg = err instanceof Error ? err.message : "Failed to load workspaces";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    if (state.kind === "expired") {
      window.location.replace("/signin?reason=session_expired");
    }
  }, [state]);

  async function handleSelect(tenantId: string): Promise<void> {
    if (activating) return;
    setActivating(tenantId);
    try {
      const res = await tenantPickerClient.activate(tenantId);
      if (res.redirect_to) {
        window.location.assign(res.redirect_to);
      } else {
        window.location.assign("/");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to activate workspace";
      setState({ kind: "error", message: msg });
      setActivating(null);
    }
  }

  return (
    <div className="reb-tenant-picker">
      <div className="reb-tenant-picker__inner">
        <h1 className="reb-tenant-picker__title">Choose a workspace</h1>
        <p className="reb-tenant-picker__subtitle">
          Your account administers more than one Ring Business workspace.
          Select one to continue.
        </p>

        {state.kind === "loading" && <p>Loading workspaces...</p>}

        {state.kind === "error" && (
          <div className="reb-card reb-error" role="alert">
            {state.message}
          </div>
        )}

        {state.kind === "ready" && state.candidates.length === 0 && (
          <div className="reb-card">
            No enterprise workspaces found for this account.
          </div>
        )}

        {state.kind === "ready" && state.candidates.length > 0 && (
          <ul className="reb-tenant-picker__list">
            {state.candidates.map((c) => (
              <li key={c.tenant_id}>
                <TenantCard
                  candidate={c}
                  onSelect={handleSelect}
                  disabled={activating !== null && activating !== c.tenant_id}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
